import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import Tooltip from "@/components/ui/tooltip";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Approve as ApproveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@/components/icons";
import type { TableColumn } from "@/types/component";

import { approvalFlowsApi } from "../services/approval-flows.api";
import { type ApprovalFlow } from "../types/approval-flows.types";
import styles from "./ApprovalFlowsPage.module.scss";

/** Tanımlı onay akışlarının listesi; buradan tasarım ekranına geçilir. */
const ApprovalFlowsPage: React.FC = () => {
  const navigate = useNavigate();

  const [flows, setFlows] = useState<ApprovalFlow[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<ApprovalFlow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [flowToMakeDefault, setFlowToMakeDefault] = useState<ApprovalFlow | null>(
    null,
  );
  const [settingDefault, setSettingDefault] = useState(false);

  const fetchFlows = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await approvalFlowsApi.getAll({
        page: currentPage,
        limit: pageSize,
        search: search.trim() || undefined,
      });
      setFlows(res.data);
      setTotal(res.total);
    } catch {
      setFlows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, search]);

  useEffect(() => {
    void fetchFlows();
  }, [fetchFlows]);

  const handleDelete = async () => {
    if (!flowToDelete) return;
    setDeleting(true);
    try {
      await approvalFlowsApi.remove(flowToDelete.id);
      toast.success("Akış silindi");
      setFlowToDelete(null);
      void fetchFlows();
    } catch {
      // 409 "akışın onay talebi var" mesajı global toast ile gösteriliyor.
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Akışı "sistemin onay akışı" yapar.
   *
   * Eğitim Yöneticisinin bu ekrana yetkisi yok ve eğitim oluştururken akış
   * seçemiyor; hangi akışın işleyeceğini burada Sistem Yöneticisi belirler.
   * Değişiklik anında geçerlidir — bundan sonra açılan tüm eğitimlerin
   * talepleri seçilen akıştan yürür.
   */
  const handleSetDefault = async () => {
    if (!flowToMakeDefault) return;
    setSettingDefault(true);
    try {
      await approvalFlowsApi.setDefault(flowToMakeDefault.id);
      toast.success(
        `"${flowToMakeDefault.title}" artık sistemin onay akışı`,
      );
      setFlowToMakeDefault(null);
      void fetchFlows();
    } catch {
      // Pasif/adımsız akış hatası global toast ile gösteriliyor.
    } finally {
      setSettingDefault(false);
    }
  };

  const columns: TableColumn<ApprovalFlow>[] = [
    {
      header: "AKIŞ GRUBU",
      key: "title",
      accessor: (row) => (
        <div>
          <div className={styles.flowTitleRow}>
            <span className={styles.flowTitle}>{row.title}</span>
            {row.isDefault && (
              <span className={styles.flowBadge}>
                <Chip size="small" type="info" variant="soft">
                  Sistemin Akışı
                </Chip>
              </span>
            )}
          </div>
          {row.description && (
            <div className={styles.flowDesc}>{row.description}</div>
          )}
        </div>
      ),
    },
    {
      header: "ADIM",
      key: "stepCount",
      width: "90px",
      accessor: (row) => `${row.stepCount} adım`,
    },
    {
      header: "DURUM",
      key: "isActive",
      width: "120px",
      accessor: (row) => (
        <Chip
          size="small"
          type={row.isActive ? "success" : "default"}
          variant="soft"
        >
          {row.isActive ? "Aktif" : "Pasif"}
        </Chip>
      ),
    },
    {
      header: "OLUŞTURAN",
      key: "createdByName",
      width: "180px",
      accessor: (row) => row.createdByName ?? "-",
    },
  ];

  const renderActions = (row: ApprovalFlow) => (
    <div className={styles.rowActions}>
      {!row.isDefault && (
        <Tooltip
          content={
            row.isActive
              ? "Sistemin onay akışı yap"
              : "Pasif akış sistemin akışı olamaz"
          }
        >
          <button
            className={styles.actionButton}
            disabled={!row.isActive}
            onClick={() => setFlowToMakeDefault(row)}
            type="button"
          >
            <ApproveIcon height={18} width={18} />
          </button>
        </Tooltip>
      )}
      <Tooltip content="Düzenle">
        <button
          className={styles.actionButton}
          onClick={() => navigate(`/dashboard/approval-flows/${row.id}`)}
          type="button"
        >
          <EditIcon color="currentColor" height={18} width={18} />
        </button>
      </Tooltip>
      <Tooltip content="Sil">
        <button
          className={`${styles.actionButton} ${styles.danger}`}
          onClick={() => setFlowToDelete(row)}
          type="button"
        >
          <DeleteIcon height={18} width={18} />
        </button>
      </Tooltip>
    </div>
  );

  return (
    <DashboardLayout>
      <Header title="Onay Akışları" />
      <PageHeader
        addButtonLabel="Yeni Akış"
        onAddNew={() => navigate("/dashboard/approval-flows/new")}
        onSearch={(value) => {
          setCurrentPage(1);
          setSearch(value);
        }}
        searchPlaceholder="Akış adı veya açıklamada ara"
        searchValue={search}
      />
      <Table
          actionsColumnHeader="AKSİYONLAR"
          columns={columns}
          currentPage={currentPage}
          data={flows}
          emptyMessage="Henüz onay akışı tanımlanmamış."
          getRowId={(row) => row.id}
          isLoading={isLoading}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSize={pageSize}
          renderActions={renderActions}
          totalRecords={total}
        />

      <ConfirmModal
        cancelText="Vazgeç"
        confirmText="Sistemin akışı yap"
        isOpen={Boolean(flowToMakeDefault)}
        loading={settingDefault}
        message={`"${flowToMakeDefault?.title}" akışı sistemin onay akışı olacak. Bundan sonra açılan eğitimlerin talepleri bu akıştan yürür; aynı türdeki önceki sistem akışının işareti kalkar. Süren talepler etkilenmez — kendi adım kopyalarıyla tamamlanırlar.`}
        onClose={() => setFlowToMakeDefault(null)}
        onConfirm={handleSetDefault}
        title="Sistemin onay akışı"
      />

      <ConfirmModal
        cancelText="Vazgeç"
        confirmText="Sil"
        isOpen={Boolean(flowToDelete)}
        loading={deleting}
        message={`"${flowToDelete?.title}" akışını silmek istediğinize emin misiniz? Akış listeden düşer ve yeni talep başlatmaz; onay talepleri geçmişte korunur. Karar bekleyen talepler süreçlerini tamamlar — onay kutusundan karara açık kalırlar.`}
        onClose={() => setFlowToDelete(null)}
        onConfirm={handleDelete}
        title="Akışı sil"
      />
    </DashboardLayout>
  );
};

export default ApprovalFlowsPage;
